import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RaceFilter, RaceStatus, DistanceType } from '../../types';
import { RegionFilterPopover } from './RegionFilterPopover';
import { DistanceFilterPopover } from './DistanceFilterPopover';
import { DateFilterPopover } from './DateFilterPopover';
import { formatDistance } from '../../utils/distance';
import { formatDateRangeLabel } from '../../utils/dateRange';
import { getRegionLabel } from '../../utils/regionMapper';
import { STATUS_LABELS } from '../../utils/raceLabels';
import './FilterChips.css';

interface FilterChipsProps {
  filter: RaceFilter;
  onFilterChange: (filter: RaceFilter) => void;
  regionCounts: Record<string, number>;
  totalCount: number;
  matchedCount: number;
}

const STATUS_VALUES: RaceStatus[] = ['open', 'upcoming', 'closed'];

interface ActiveBadge {
  key: string;
  kind: 'status' | 'distance' | 'region' | 'date';
  label: string;
  onRemove: () => void;
}

export function FilterChips({
  filter,
  onFilterChange,
  regionCounts,
  totalCount,
  matchedCount,
}: FilterChipsProps) {
  const { t } = useTranslation('races');

  const toggleStatus = (status: RaceStatus) => {
    const current = filter.status ?? [];
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    onFilterChange({ ...filter, status: next.length > 0 ? next : undefined });
  };

  const setDistances = (distances: DistanceType[]) =>
    onFilterChange({ ...filter, distances: distances.length > 0 ? distances : undefined });

  const setRegions = (regions: string[]) =>
    onFilterChange({ ...filter, region: regions.length > 0 ? regions : undefined });

  const setDateRange = (range: Pick<RaceFilter, 'startDate' | 'endDate'>) =>
    onFilterChange({ ...filter, ...range });

  const activeBadges = useMemo<ActiveBadge[]>(() => {
    const badges: ActiveBadge[] = [];
    for (const s of filter.status ?? []) {
      badges.push({
        key: `status-${s}`,
        kind: 'status',
        label: STATUS_LABELS[s] ?? s,
        onRemove: () => {
          const next = (filter.status ?? []).filter((x) => x !== s);
          onFilterChange({ ...filter, status: next.length > 0 ? next : undefined });
        },
      });
    }
    for (const d of filter.distances ?? []) {
      badges.push({
        key: `distance-${d}`,
        kind: 'distance',
        label: formatDistance(d),
        onRemove: () => {
          const next = (filter.distances ?? []).filter((x) => x !== d);
          onFilterChange({ ...filter, distances: next.length > 0 ? next : undefined });
        },
      });
    }
    for (const r of filter.region ?? []) {
      badges.push({
        key: `region-${r}`,
        kind: 'region',
        label: getRegionLabel(r),
        onRemove: () => {
          const next = (filter.region ?? []).filter((x) => x !== r);
          onFilterChange({ ...filter, region: next.length > 0 ? next : undefined });
        },
      });
    }
    if (filter.startDate || filter.endDate) {
      badges.push({
        key: 'date-range',
        kind: 'date',
        label: formatDateRangeLabel(filter.startDate, filter.endDate),
        onRemove: () => onFilterChange({ ...filter, startDate: undefined, endDate: undefined }),
      });
    }
    return badges;
  }, [filter, onFilterChange]);

  const hasAnyFilter = activeBadges.length > 0;
  const isEmpty = matchedCount === 0 && totalCount > 0;
  const resetAll = () => onFilterChange({});

  return (
    <div className="filter-chips" role="toolbar" aria-label={t('filter.label')}>
      <div className="filter-top">
        <div className="filter-chip-group status-group" role="group" aria-label={t('filter.status.label')}>
          {STATUS_VALUES.map((value) => {
            const active = filter.status?.includes(value) ?? false;
            return (
              <button
                key={value}
                className={`status-chip status-${value} ${active ? 'active' : ''}`}
                onClick={() => toggleStatus(value)}
                aria-pressed={active}
              >
                {t(`filter.status.${value}`)}
              </button>
            );
          })}
        </div>

        <div className="filter-chip-group detail-group" role="group" aria-label={t('filter.detail')}>
          <DistanceFilterPopover
            selectedDistances={filter.distances ?? []}
            onDistancesChange={setDistances}
          />
          <DateFilterPopover
            startDate={filter.startDate}
            endDate={filter.endDate}
            onDateRangeChange={setDateRange}
          />
          <RegionFilterPopover
            selectedRegions={filter.region ?? []}
            onRegionsChange={setRegions}
            regionCounts={regionCounts}
          />
        </div>
      </div>

      <div
        className={`filter-summary ${isEmpty ? 'is-empty' : ''}`}
        aria-live="polite"
      >
        <div
          className="filter-counter"
          aria-label={`총 ${totalCount}개 중 ${matchedCount}개 매칭`}
        >
          <span className="filter-counter-matched display">{matchedCount}</span>
          <span className="filter-counter-separator" aria-hidden="true">
            /
          </span>
          <span className="filter-counter-total display">{totalCount}</span>
          <span className="filter-counter-label eyebrow">Races</span>
        </div>

        {hasAnyFilter && (
          <ul className="filter-active-badges" aria-label="적용된 필터">
            {activeBadges.map((badge) => (
              <li key={badge.key}>
                <button
                  type="button"
                  className={`filter-active-badge filter-active-badge--${badge.kind}`}
                  onClick={badge.onRemove}
                  aria-label={`${badge.label} 필터 제거`}
                >
                  <span className="filter-active-badge-label">{badge.label}</span>
                  <span className="filter-active-badge-remove" aria-hidden="true">
                    ×
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {isEmpty && (
          <span className="filter-empty-hint">{t('filter.emptyHint')}</span>
        )}

        {hasAnyFilter && (
          <button
            className="filter-reset-all"
            onClick={resetAll}
            aria-label={t('filter.resetAll')}
          >
            <span className="filter-reset-all-icon" aria-hidden="true">
              ↻
            </span>
            <span className="filter-reset-all-label">{t('filter.resetAll')}</span>
          </button>
        )}
      </div>
    </div>
  );
}
